import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import StaffManagement from './pages/StaffManagement';
import BookingCalendar from './pages/BookingCalendar';
import Messages from './pages/Messages';
import Approvals from './pages/Approvals';
import CSAgentTraining from './pages/CSAgentTraining';
import StudioClosures from './pages/StudioClosures';
import Analytics from './pages/Analytics';
import Operations from './pages/Operations';
import Studios from './pages/Studios';

export function getUser() {
  const token = localStorage.getItem('access_token');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      return null;
    }
    let identity = payload.sub;
    if (typeof identity === 'string') identity = JSON.parse(identity);
    return identity;
  } catch {
    return null;
  }
}

function ProtectedRoute({ children, allowedRoles }) {
  const user = getUser();
  if (!user) return <Navigate to="/login" />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" />;
  return children;
}

// SVG icons for tab bar
const icons = {
  approvals: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </svg>
  ),
  dashboard: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  bookings: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  messages: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  customers: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  more: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/>
    </svg>
  ),
  staff: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
    </svg>
  ),
  logout: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  analytics: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
    </svg>
  ),
};

function Layout({ children }) {
  const user = getUser();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  // Messages page tells us when to hide the tab bar (only when viewing a thread on mobile)
  const [hideTabBar, setHideTabBar] = useState(false);

  // Expose the setter globally so Messages can control it
  useEffect(() => {
    window.__setHideTabBar = setHideTabBar;
    return () => { delete window.__setHideTabBar; };
  }, []);

  const isMessagesPage = location.pathname === '/messages';

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    const handleClick = () => setMoreOpen(false);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [moreOpen]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/login';
  };

  const isStaffVisible = user?.role === 'owner';

  const tabs = [
    { to: '/', label: 'Home', icon: icons.dashboard, end: true },
    { to: '/bookings', label: 'Bookings', icon: icons.bookings },
    { to: '/messages', label: 'Messages', icon: icons.messages },
    { to: '/customers', label: 'Customers', icon: icons.customers },
  ];

  return (
    <div className="app-layout">
      <main className={`main-content ${isMessagesPage ? 'messages-active' : ''}`}>
        {children}
      </main>

      {/* Bottom Tab Bar — hidden only when viewing a message thread on mobile */}
      {!hideTabBar && (
        <nav className="tab-bar">
          {tabs.map(tab => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) => `tab-item ${isActive ? 'active' : ''}`}
            >
              {tab.icon}
              <span className="tab-label">{tab.label}</span>
            </NavLink>
          ))}
          <div
            className={`tab-item ${moreOpen ? 'active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setMoreOpen(!moreOpen); }}
          >
            {icons.more}
            <span className="tab-label">More</span>
          </div>

          {moreOpen && (
            <div className="more-menu" onClick={(e) => e.stopPropagation()}>
              {(user?.role === 'owner' || user?.role === 'manager') && (
                <NavLink to="/analytics" className="more-menu-item">
                  {icons.analytics}<span>Analytics</span>
                </NavLink>
              )}
              {user?.role === 'owner' && (
                <NavLink to="/approvals" className="more-menu-item">
                  {icons.approvals}
                  <span>Agent Approvals</span>
                </NavLink>
              )}
              {user?.role === 'owner' && (
                <NavLink to="/cs-agent-training" className="more-menu-item">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                  <span>CS Agent Training</span>
                </NavLink>
              )}
              {(user?.role === 'owner' || user?.role === 'manager') && (
                <NavLink to="/operations" className="more-menu-item">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 7h-9"/><path d="M14 17H5"/><circle cx="17" cy="17" r="3"/><circle cx="7" cy="7" r="3"/>
                  </svg>
                  <span>Operations</span>
                </NavLink>
              )}
              {(user?.role === 'owner' || user?.role === 'manager') && (
                <NavLink to="/studios" className="more-menu-item">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-6 9 6v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M9 22V12h6v10"/>
                  </svg>
                  <span>Studios</span>
                </NavLink>
              )}
              {(user?.role === 'owner' || user?.role === 'manager') && (
                <NavLink to="/studio-closures" className="more-menu-item">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="15" x2="16" y2="15"/>
                  </svg>
                  <span>Closed Dates</span>
                </NavLink>
              )}
              {isStaffVisible && (
                <NavLink to="/staff" className="more-menu-item">
                  {icons.staff}
                  <span>Staff Management</span>
                </NavLink>
              )}
              {user && (
                <div className="more-menu-user">
                  <span className="more-menu-user-name">{user.name}</span>
                  <span className="more-menu-user-role">{user.role} · {user.country}</span>
                </div>
              )}
              <button className="more-menu-item more-menu-logout" onClick={handleLogout}>
                {icons.logout}
                <span>Log out</span>
              </button>
            </div>
          )}
        </nav>
      )}
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={
        <ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>
      } />
      <Route path="/bookings" element={
        <ProtectedRoute><Layout><BookingCalendar /></Layout></ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute><Layout><Messages /></Layout></ProtectedRoute>
      } />
      <Route path="/customers" element={
        <ProtectedRoute><Layout><Customers /></Layout></ProtectedRoute>
      } />
      <Route path="/customers/:id" element={
        <ProtectedRoute><Layout><CustomerDetail /></Layout></ProtectedRoute>
      } />
      <Route path="/staff" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <Layout><StaffManagement /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute allowedRoles={['owner', 'manager']}>
          <Layout><Analytics /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/operations" element={
        <ProtectedRoute allowedRoles={['owner', 'manager']}>
          <Layout><Operations /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/studios" element={
        <ProtectedRoute allowedRoles={['owner', 'manager']}>
          <Layout><Studios /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/studio-closures" element={
        <ProtectedRoute allowedRoles={['owner', 'manager']}>
          <Layout><StudioClosures /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/approvals" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <Layout><Approvals /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/cs-agent-training" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <Layout><CSAgentTraining /></Layout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}